import { useState, useEffect, useCallback } from 'react'
import { toast } from '../services/toastService'
import { useAuth } from '../context/AuthContext'
import { authAPI } from '../services/api'
import { validatePassword, validatePasswordMatch } from '../utils/validators'
import { trackButtonClick } from '../utils/analytics'
import { createLogger } from '../utils/logger'

const logger = createLogger('useProfileManagement')

/**
 * Reusable hook for profile management
 * Handles profile editing, password changes, and language switching
 * 
 * @param {object} user - Current user object from useAuth
 * @param {object} i18n - i18n instance from useTranslation
 * @param {function} t - Translation function
 * @returns {object} Profile management state and handlers
 */
export function useProfileManagement(user, i18n, t) {
  const { updateProfile } = useAuth()
  
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    language: ''
  })
  const [profileErrors, setProfileErrors] = useState({})
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  
  // Avatar upload state
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)

  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [passwordErrors, setPasswordErrors] = useState({})
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Initialize profile form when user changes
  useEffect(() => {
    if (user) {
      setProfileFormData({
        firstName: user.firstName || user.name?.split(' ')[0] || '',
        lastName: user.lastName || user.name?.split(' ')[1] || '',
        email: user.email || '',
        phone: user.phone || '',
        language: user.language || i18n.language || 'en'
      })
    }
  }, [user, i18n.language])

  // Profile form change handler
  const handleProfileChange = useCallback(async (e) => {
    const { name, value } = e.target
    setProfileFormData(prev => ({ ...prev, [name]: value }))
    if (profileErrors[name]) {
      setProfileErrors(prev => ({ ...prev, [name]: '' }))
    }

    // If language changed, immediately switch app language and save to backend
    if (name === 'language' && value !== i18n.language) {
      try {
        await i18n.changeLanguage(value)
        // Save language preference to backend and update user context
        await updateProfile({ language: value })
        toast.success(t('settings.languageChanged', 'Language changed successfully'))
      } catch (error) {
        logger.error('Failed to change language', error)
        toast.error(t('settings.languageChangeFailed', 'Failed to change language'))
      }
    }
  }, [profileErrors, i18n, t, updateProfile])

  // Avatar change handler
  const handleAvatarChange = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.invalidImageType', 'Please select an image file'))
      return
    }
    
    // Validate file size (max 500KB for avatars - smaller than sighting images)
    const maxSize = 500 * 1024 // 500KB
    if (file.size > maxSize) {
      toast.error(t('profile.imageTooLarge', 'Avatar must be smaller than 500KB'))
      return
    }
    
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
  }, [t])
  
  // Remove avatar handler
  const handleRemoveAvatar = useCallback(() => {
    setAvatarFile(null)
    setAvatarPreview(null)
  }, [])
  
  // Profile submit handler
  const handleProfileSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    trackButtonClick('submit_profile_update', 'profile_tab', { has_avatar_change: !!avatarFile })
    
    setIsSavingProfile(true)

    let avatarUrl = null
    
    // Upload avatar if changed (use existing image upload endpoint)
    if (avatarFile) {
      const formData = new FormData()
      formData.append('image', avatarFile) // Note: 'image' not 'avatar' - matches sighting upload
      
      try {
        const uploadResponse = await authAPI.uploadImage(formData)
        avatarUrl = uploadResponse.data.url
        toast.success(t('profile.avatarUpdated', 'Avatar updated successfully!'))
      } catch (error) {
        logger.error('Avatar upload failed', error)
        toast.error(t('profile.avatarUploadFailed', 'Failed to upload avatar'))
        setIsSavingProfile(false)
        return // Stop profile update if avatar upload fails
      }
    }
    
    // Prepare profile data with avatar URL if uploaded
    const profileData = { ...profileFormData }
    if (avatarUrl) {
      profileData.avatar = avatarUrl
    }
    
    // Language is already saved when dropdown changes, just update other fields
    const result = await updateProfile(profileData)
    if (result.success) {
      setIsEditingProfile(false)
      setAvatarFile(null)
      setAvatarPreview(null)
      toast.success(t('profile.updateSuccess', 'Profile updated successfully!'))
    } else {
      toast.error(result.error || t('profile.updateFailed', 'Failed to update profile'))
    }
    
    setIsSavingProfile(false)
  }, [profileFormData, avatarFile, updateProfile, t])

  // Cancel profile edit
  const handleCancelEdit = useCallback(() => {
    setProfileFormData({
      firstName: user.firstName || user.name?.split(' ')[0] || '',
      lastName: user.lastName || user.name?.split(' ')[1] || '',
      email: user.email || '',
      phone: user.phone || '',
      language: user.language || i18n.language || 'en'
    })
    setProfileErrors({})
    setIsEditingProfile(false)
  }, [user, i18n.language])

  // Password form change handler
  const handlePasswordChange = useCallback((e) => {
    const { name, value } = e.target
    setPasswordData(prev => ({ ...prev, [name]: value }))
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }))
    }
  }, [passwordErrors])

  // Password submit handler
  const handlePasswordSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    // Check if user has Google OAuth linked
    if (user?.hasGoogleId) {
      toast.error(t('profile.errors.oauthOnlyAccount', 'Your account is linked with Google. Password changes are not available for OAuth accounts.'))
      return
    }
    
    trackButtonClick('submit_password_change', 'profile_tab')
    
    // Validation
    const errors = {}
    if (!passwordData.currentPassword) {
      errors.currentPassword = t('profile.errors.currentPasswordRequired', 'Current password is required')
    }
    const passwordValidation = validatePassword(passwordData.newPassword, 8)
    if (!passwordValidation.valid) {
      errors.newPassword = passwordValidation.error
    }
    const passwordMatchValidation = validatePasswordMatch(passwordData.newPassword, passwordData.confirmPassword)
    if (!passwordMatchValidation.valid) {
      errors.confirmPassword = passwordMatchValidation.error
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors)
      return
    }

    setIsChangingPassword(true)
    
    const result = await updateProfile({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword
    })
    
    if (result.success) {
      toast.success(t('toast.success.passwordChanged'))
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setShowPasswordSection(false)
    } else {
      toast.error(result.error || t('profile.errors.passwordChangeFailed', 'Failed to change password'))
    }
    
    setIsChangingPassword(false)
  }, [passwordData, updateProfile, t, user])

  return {
    // Profile editing
    isEditingProfile,
    setIsEditingProfile,
    profileFormData,
    profileErrors,
    isSavingProfile,
    handleProfileChange,
    handleProfileSubmit,
    handleCancelEdit,
    
    // Avatar upload
    avatarFile,
    avatarPreview,
    handleAvatarChange,
    handleRemoveAvatar,
    
    // Password change
    showPasswordSection,
    setShowPasswordSection,
    passwordData,
    passwordErrors,
    isChangingPassword,
    handlePasswordChange,
    handlePasswordSubmit
  }
}
